import { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../../config";
import { AuthContext } from "../../context/AuthContext";
import { isAdminUser } from "../../utils/analytics";
import Leftbar from "../../components/LeftBar/lefbar";
import Topbar from "../../components/topbar/Topbar";
import toast from "react-hot-toast";
import "./CampaignsPage.css";

const CATEGORIES = ["All", "Just Chatting", "IRL", "Politics", "Music", "Gaming", "Entertainment", "Sports", "Education", "Lifestyle", "Technology"];

const SORT_OPTIONS = [
  { value: "featured",  label: "Featured" },
  { value: "newest",    label: "Newest" },
  { value: "budget",    label: "Highest Budget" },
  { value: "cpm",       label: "Highest CPM" },
  { value: "spent",     label: "Most Paid Out" },
  { value: "creators",  label: "Most Creators" },
];

const PLATFORM_META = {
  youtube:   { label: "YouTube",   color: "#ff0000", bg: "#ff0000",
    icon: (s=16) => <svg width={s} height={s} viewBox="0 0 24 24" fill="#ff0000"><path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2C0 8.1 0 12 0 12s0 3.9.5 5.8a3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1C24 15.9 24 12 24 12s0-3.9-.5-5.8zM9.75 15.5v-7l6.5 3.5-6.5 3.5z"/></svg> },
  tiktok:    { label: "TikTok",    color: "#fff",    bg: "#010101",
    icon: (s=16) => <svg width={s} height={s} viewBox="0 0 24 24" fill="#fff"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.77a4.85 4.85 0 0 1-1.01-.08z"/></svg> },
  instagram: { label: "Instagram", color: "#e1306c", bg: "linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)",
    icon: (s=16) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="#e1306c" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="4.5"/><circle cx="17.5" cy="6.5" r="1.2" fill="#e1306c" stroke="none"/></svg> },
  x:         { label: "X",         color: "#fff",    bg: "#000",
    icon: (s=14) => <svg width={s} height={s} viewBox="0 0 24 24" fill="#fff"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.261 5.632zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> },
  facebook:  { label: "Facebook",  color: "#1877f2", bg: "#1877f2",
    icon: (s=16) => <svg width={s} height={s} viewBox="0 0 24 24" fill="#1877f2"><path d="M24 12.07C24 5.41 18.63 0 12 0S0 5.41 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.04V9.41c0-3.02 1.8-4.7 4.54-4.7 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.95.93-1.95 1.88v2.27h3.32l-.53 3.49h-2.79V24C19.61 23.1 24 18.1 24 12.07z"/></svg> },
  rumble:    { label: "Rumble",    color: "#85c742", bg: "#1c2b0e",
    icon: (s=16) => <img src="https://rumble.com/apple-touch-icon.png" width={s} height={s} style={{borderRadius: s > 14 ? '4px' : '3px', display:'block'}} alt="Rumble" /> },
};

const PLATFORMS = Object.entries(PLATFORM_META).map(([value, m]) => ({
  value,
  icon: m.icon(16),
}));

const NICHE_COVER = {
  Gaming:          'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=800&q=80&fit=crop&h=450',
  IRL:             'https://images.unsplash.com/photo-1493612276216-ee3925520721?w=800&q=80&fit=crop&h=450',
  Sports:          'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=800&q=80&fit=crop&h=450',
  Music:           'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=800&q=80&fit=crop&h=450',
  Esports:         'https://images.unsplash.com/photo-1560253023-3ec5d502959f?w=800&q=80&fit=crop&h=450',
  Creative:        'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=800&q=80&fit=crop&h=450',
  'Just Chatting': 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&q=80&fit=crop&h=450',
  Variety:         'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800&q=80&fit=crop&h=450',
  Tech:            'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&q=80&fit=crop&h=450',
  Media:           'https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=800&q=80&fit=crop&h=450',
  'AI/ML':         'https://images.unsplash.com/photo-1677442135703-1787eea5ce01?w=800&q=80&fit=crop&h=450',
  Finance:         'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&q=80&fit=crop&h=450',
  Startup:         'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800&q=80&fit=crop&h=450',
  Design:          'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=800&q=80&fit=crop&h=450',
  Developer:       'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=800&q=80&fit=crop&h=450',
  Productivity:    'https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=800&q=80&fit=crop&h=450',
};

const CATEGORY_COLORS = {
  "Tech": "#3b82f6", "Productivity": "#8b5cf6", "Gaming": "#f97316",
  "Finance": "#10b981", "AI/ML": "#06b6d4", "Developer": "#6366f1",
  "Startup": "#eab308", "Design": "#ec4899", "Media": "#dc2626", "Other": "#71717a",
};

function VerifiedBadge({ type, size = 14 }) {
  if (!type || type === 'none') return null;
  if (type === 'gold') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" style={{ flexShrink: 0, display: 'inline-block' }}>
        <circle cx="12" cy="12" r="10" fill="#f59e0b"/>
        <path d="M8.5 12.5l2.5 2.5 4.5-5" stroke="#fff" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ flexShrink: 0, display: 'inline-block' }}>
      <circle cx="12" cy="12" r="10" fill="#3b82f6"/>
      <path d="M8.5 12.5l2.5 2.5 4.5-5" stroke="#fff" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function timeAgo(date) {
  const diff = (Date.now() - new Date(date)) / 1000;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}


function CampaignCard({ campaign, appliedIds, onApply, onManage, isOwner, onViewDetail }) {
  const applied = appliedIds.has(campaign._id);
  const catColor = CATEGORY_COLORS[campaign.category] || CATEGORY_COLORS.Other;
  const banner = campaign.bannerImage || NICHE_COVER[campaign.category] || NICHE_COVER.Variety;
  const pct = campaign.totalBudget > 0
    ? Math.min(((campaign.totalBudget - (campaign.spentBudget || 0)) / campaign.totalBudget) * 100, 100)
    : 100;

  return (
    <div className="cp-card" onClick={() => onViewDetail(campaign)}>
      {/* Image with text overlay */}
      <div className="cp-card-img-wrap" style={{ backgroundImage: `url(${banner})` }}>
        <div className="cp-card-img-gradient" />
        <div className="cp-card-img-overlay-text">
          <div className="cp-card-img-title">{campaign.title}</div>
        </div>
      </div>

      {/* Body */}
      <div className="cp-card-body">
        {/* Meta row */}
        <div className="cp-card-meta">
          <div className="cp-card-brand">
            {campaign.brandLogo
              ? <img src={campaign.brandLogo} alt={campaign.brandName} className="cp-card-brand-logo" onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
              : null
            }
            <div className="cp-card-brand-initial" style={campaign.brandLogo ? { display: 'none' } : {}}>{campaign.brandName?.[0]?.toUpperCase()}</div>
            <div className="cp-card-brand-info">
              <div className="cp-card-brand-name-row">
                <span className="cp-card-brand-name">{campaign.brandName}</span>
                <VerifiedBadge type={campaign.brandVerified} size={13} />
              </div>
              {campaign.brandUsername && (
                <span className="cp-card-brand-username">{campaign.brandUsername}</span>
              )}
            </div>
            <span className="cp-card-dot">·</span>
            <span className="cp-card-time">{timeAgo(campaign.createdAt)}</span>
          </div>
        </div>

        <p className="cp-card-desc">{campaign.description}</p>

        {isOwner ? (
          <button className="cp-card-join-btn manage" onClick={e => { e.stopPropagation(); onManage(campaign); }}>
            Manage · {campaign.applicationCount} applied
          </button>
        ) : (
          <button
            className={`cp-card-join-btn${applied ? " applied" : ""}`}
            onClick={e => { e.stopPropagation(); onViewDetail(campaign); }}
            disabled={applied}
          >
            {applied ? "✓ Applied" : "Join Campaign"}
          </button>
        )}

        {/* Platform icons */}
        {campaign.platforms?.length > 0 && (
          <div className="cp-card-platforms">
            {campaign.platforms.map(p => (
              <span key={p} className="cp-card-platform-icon" title={PLATFORM_META[p]?.label || p}>
                {PLATFORM_META[p]?.icon(12) || null}
              </span>
            ))}
          </div>
        )}

        {/* Budget footer */}
        <div className="cp-card-footer">
          <span className="cp-budget-text">${(campaign.spentBudget || 0).toLocaleString()}/${campaign.totalBudget.toLocaleString()}</span>
          <div className="cp-card-footer-right">
            {campaign.applicationCount > 0 && (
              <span className="cp-card-applicants">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                {campaign.applicationCount}
              </span>
            )}
            <span className="cp-card-rate">{campaign.payType === "pool" ? `$${Number(campaign.totalBudget).toLocaleString()} pool` : `$${campaign.payoutPer1K}/1K`}</span>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="cp-card-progress-bar">
        <div className="cp-card-progress-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function CreateCampaignModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    brandName: "", title: "", description: "", category: "Entertainment",
    payoutPer1K: "", totalBudget: "", requirements: "", campaignBrief: "", status: "active",
  });
  const [brandLogoFile, setBrandLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const bannerRef = useRef();
  const logoRef = useRef();

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleBanner = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBannerFile(file);
    setBannerPreview(URL.createObjectURL(file));
  };

  const handleLogo = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBrandLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.brandName || !form.title || !form.description || !form.payoutPer1K || !form.totalBudget) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (brandLogoFile) fd.append("brandLogo", brandLogoFile);
      if (bannerFile) fd.append("bannerImage", bannerFile);

      const res = await axiosInstance.post("/campaigns", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Campaign created!");
      onCreated(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to create campaign");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cp-modal-overlay" onClick={onClose}>
      <div className="cp-modal" onClick={e => e.stopPropagation()}>
        <div className="cp-modal-header">
          <h2>Create Campaign</h2>
          <button className="cp-modal-close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <form className="cp-modal-form" onSubmit={handleSubmit}>
          {/* Banner upload */}
          <div className="cp-form-banner-upload" onClick={() => bannerRef.current?.click()}
            style={bannerPreview ? { backgroundImage: `url(${bannerPreview})` } : {}}>
            {!bannerPreview && (
              <div className="cp-form-banner-placeholder">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
                <span>Upload banner image</span>
              </div>
            )}
            <input ref={bannerRef} type="file" accept="image/*" onChange={handleBanner} style={{ display: "none" }} />
          </div>

          <div className="cp-form-row">
            {/* Logo upload */}
            <div className="cp-form-logo-upload" onClick={() => logoRef.current?.click()}>
              {logoPreview ? (
                <img src={logoPreview} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }} />
              ) : (
                <span style={{ fontSize: 22 }}>+</span>
              )}
              <input ref={logoRef} type="file" accept="image/*" onChange={handleLogo} style={{ display: "none" }} />
            </div>

            <div className="cp-form-col">
              <input className="cp-form-input" placeholder="Brand name *" value={form.brandName}
                onChange={e => set("brandName", e.target.value)} />
              <select className="cp-form-input" value={form.category} onChange={e => set("category", e.target.value)}>
                {CATEGORIES.filter(c => c !== "All").map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <input className="cp-form-input" placeholder="Campaign title *" value={form.title}
            onChange={e => set("title", e.target.value)} />

          <textarea className="cp-form-input cp-form-textarea" placeholder="Description *" value={form.description}
            onChange={e => set("description", e.target.value)} rows={3} />

          <textarea className="cp-form-input cp-form-textarea" placeholder="Requirements (what you're looking for in a clip)" value={form.requirements}
            onChange={e => set("requirements", e.target.value)} rows={2} />

          <textarea className="cp-form-input cp-form-textarea" placeholder="Full campaign brief / guide -- paste your whole doc here. Public, shown to clippers, and what the review bot checks submissions against." value={form.campaignBrief}
            onChange={e => set("campaignBrief", e.target.value)} rows={6} />

          <div className="cp-form-row">
            <div className="cp-form-field">
              <label className="cp-form-label">Payout per 1K views ($) *</label>
              <input className="cp-form-input" type="number" min="0.01" step="0.01" placeholder="e.g. 3.00"
                value={form.payoutPer1K} onChange={e => set("payoutPer1K", e.target.value)} />
            </div>
            <div className="cp-form-field">
              <label className="cp-form-label">Total budget ($) *</label>
              <input className="cp-form-input" type="number" min="1" placeholder="e.g. 1000"
                value={form.totalBudget} onChange={e => set("totalBudget", e.target.value)} />
            </div>
          </div>

          <select className="cp-form-input" value={form.status} onChange={e => set("status", e.target.value)}>
            <option value="active">Active (visible now)</option>
            <option value="draft">Draft (hidden)</option>
          </select>

          <button className="cp-form-submit" type="submit" disabled={saving}>
            {saving ? "Creating..." : "Create Campaign"}
          </button>
        </form>
      </div>
    </div>
  );
}

const PLATFORM_ICONS = Object.fromEntries(
  Object.entries(PLATFORM_META).map(([k, m]) => [k, m.icon(20)])
);
const PLATFORM_LABELS = Object.fromEntries(
  Object.entries(PLATFORM_META).map(([k, m]) => [k, m.label])
);

const SUBMIT_PLATFORMS = ["instagram", "tiktok", "youtube"];
const SUBMISSION_STATUS_LABEL = { pending: "Reviewing…", approved: "Approved", flagged: "In review", rejected: "Rejected" };

function CampaignDetailModal({ campaign, applied: initApplied, application, onClose, onApplied }) {
  const [applied, setApplied] = useState(initApplied);
  const [saving, setSaving] = useState(false);
  const banner = campaign.bannerImage;
  const isApprovedApplicant = application?.status === "approved";

  const [submissions, setSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [clipUrl, setClipUrl] = useState("");
  const submitPlatformOptions = (campaign.platforms || []).filter(p => SUBMIT_PLATFORMS.includes(p));
  const [clipPlatform, setClipPlatform] = useState((submitPlatformOptions[0] || SUBMIT_PLATFORMS[0]));
  const [submittingClip, setSubmittingClip] = useState(false);

  useEffect(() => {
    if (!isApprovedApplicant) return;
    setLoadingSubmissions(true);
    axiosInstance.get("/clip-submissions/mine")
      .then(res => {
        const mine = (res.data.submissions || []).filter(s => (s.campaign?._id || s.campaign) === campaign._id);
        setSubmissions(mine);
      })
      .catch(() => {})
      .finally(() => setLoadingSubmissions(false));
  }, [isApprovedApplicant, campaign._id]);

  const handleSubmitClip = async () => {
    if (!clipUrl.trim()) return toast.error("Paste your posted clip's link first");
    setSubmittingClip(true);
    try {
      const res = await axiosInstance.post("/clip-submissions", {
        campaignId: campaign._id,
        platform: clipPlatform,
        url: clipUrl.trim(),
      });
      setSubmissions(prev => [res.data.submission, ...prev]);
      setClipUrl("");
      toast.success("Clip submitted — reviewing now");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to submit clip");
    } finally {
      setSubmittingClip(false);
    }
  };
  const remaining = Math.max(0, campaign.totalBudget - (campaign.spentBudget || 0));
  const pct = campaign.totalBudget > 0 ? Math.min(((campaign.totalBudget - (campaign.spentBudget || 0)) / campaign.totalBudget) * 100, 100) : 100;
  const reqLines = campaign.requirements
    ? campaign.requirements.split(/\.\s+|\n/).map(s => s.trim().replace(/\.$/, '')).filter(Boolean)
    : [];
  const platformLabel = campaign.platforms?.length === 1
    ? (PLATFORM_LABELS[campaign.platforms[0]] || campaign.platforms[0]) + " only"
    : campaign.platforms?.map(p => PLATFORM_LABELS[p] || p).join(", ");

  const handleApply = async () => {
    setSaving(true);
    try {
      await axiosInstance.post(`/campaigns/${campaign._id}/apply`, {});
      toast.success("Application submitted!");
      setApplied(true);
      onApplied(campaign._id);
    } catch (err) {
      if (err.response?.status === 409) {
        setApplied(true);
        onApplied(campaign._id);
      } else {
        toast.error(err.response?.data?.error || "Failed to apply");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cp-modal-overlay" onClick={onClose}>
      <div className="cp-detail-modal" onClick={e => e.stopPropagation()}>

        {/* Hero */}
        <div
          className="cp-detail-hero"
          style={banner ? { backgroundImage: `url(${banner})` } : {}}
        >
          {!banner && (
            <div className="cp-detail-hero-icon">
              {campaign.brandLogo
                ? <img src={campaign.brandLogo} alt={campaign.brandName} />
                : <span>{campaign.brandName?.[0]?.toUpperCase()}</span>}
            </div>
          )}
          {banner && <div className="cp-detail-hero-fade" />}
          <button className="cp-detail-close" onClick={onClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Scrollable body */}
        <div className="cp-detail-body">

          {/* Brand row */}
          <div className="cp-detail-brand-row">
            {campaign.brandLogo
              ? <img src={campaign.brandLogo} alt="" className="cp-detail-brand-logo" onError={e => e.target.style.display='none'} />
              : <div className="cp-detail-brand-initial">{campaign.brandName?.[0]?.toUpperCase()}</div>}
            <span className="cp-detail-brand-name">{campaign.brandName}</span>
            <VerifiedBadge type={campaign.brandVerified} size={14} />
            {platformLabel && <span className="cp-detail-platform-label">· {platformLabel}</span>}
          </div>

          {/* Title */}
          <h2 className="cp-detail-title">{campaign.title}</h2>

          {/* Budget row */}
          <div className="cp-detail-budget-row">
            <span className="cp-detail-spent">${(campaign.spentBudget || 0).toLocaleString()}</span>
            <span className="cp-detail-total">/${campaign.totalBudget.toLocaleString()}</span>
            <div className="cp-detail-progress">
              <div className="cp-detail-progress-fill" style={{ width: `${pct}%` }} />
            </div>
            <span className="cp-detail-cat-tag">{campaign.category}</span>
            <span className="cp-detail-rate-tag">${campaign.payoutPer1K}/1K</span>
          </div>

          {/* Description */}
          <p className="cp-detail-desc">{campaign.description}</p>

          {/* Apply row */}
          <div className="cp-detail-apply-row">
            <button
              className={`cp-detail-apply-btn${applied ? " applied" : ""}`}
              onClick={() => !applied && handleApply()}
              disabled={applied || saving}
            >
              {applied ? "✓ Applied" : saving ? "Submitting…" : "Apply"}
            </button>
            {remaining > 0 && (
              <span className="cp-detail-remaining">${remaining.toLocaleString()} left</span>
            )}
          </div>

          {/* Submit clip (approved applicants only) */}
          {isApprovedApplicant && (
            <>
              <div className="cp-detail-divider" />
              <div className="cp-detail-section">
                <div className="cp-detail-section-label">Submit a clip</div>
                <div className="cp-detail-submit-row">
                  <select
                    className="cp-detail-submit-platform"
                    value={clipPlatform}
                    onChange={e => setClipPlatform(e.target.value)}
                  >
                    {(submitPlatformOptions.length ? submitPlatformOptions : SUBMIT_PLATFORMS).map(p => (
                      <option key={p} value={p}>{PLATFORM_LABELS[p] || p}</option>
                    ))}
                  </select>
                  <input
                    className="cp-detail-submit-input"
                    placeholder="Paste your posted clip's link"
                    value={clipUrl}
                    onChange={e => setClipUrl(e.target.value)}
                  />
                  <button
                    className="cp-detail-submit-btn"
                    onClick={handleSubmitClip}
                    disabled={submittingClip}
                  >
                    {submittingClip ? "Submitting…" : "Submit"}
                  </button>
                </div>

                {!loadingSubmissions && submissions.length > 0 && (
                  <div className="cp-detail-submissions-list">
                    {submissions.map(s => (
                      <div key={s._id} className="cp-detail-submission-row">
                        <span className="cp-detail-submission-platform">{PLATFORM_LABELS[s.platform] || s.platform}</span>
                        <a href={s.url} target="_blank" rel="noreferrer" className="cp-detail-submission-url">{s.url}</a>
                        <span className={`cp-detail-submission-status ${s.status}`}>
                          {SUBMISSION_STATUS_LABEL[s.status] || s.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Requirements */}
          {reqLines.length > 0 && (
            <>
              <div className="cp-detail-divider" />
              <div className="cp-detail-section">
                <div className="cp-detail-section-label">Requirements</div>
                {reqLines.map((line, i) => (
                  <div key={i} className="cp-detail-req-line">{line}</div>
                ))}
              </div>
            </>
          )}

          {/* Full brief -- long-form, what the review bot checks against */}
          {campaign.campaignBrief && (
            <>
              <div className="cp-detail-divider" />
              <div className="cp-detail-section">
                <details className="cp-detail-full-brief">
                  <summary className="cp-detail-section-label">Full campaign brief</summary>
                  <pre className="cp-detail-brief-text">{campaign.campaignBrief}</pre>
                </details>
              </div>
            </>
          )}

          {/* Earnings */}
          {campaign.platforms?.length > 0 && (
            <>
              <div className="cp-detail-divider" />
              <div className="cp-detail-section">
                <div className="cp-detail-section-label">Earnings</div>
                <div className="cp-detail-earn-bar">
                  <div className="cp-detail-earn-logos">
                    {campaign.platforms.map(p => (
                      <div key={p} className="cp-detail-earn-logo" style={{ background: PLATFORM_META[p]?.bg || '#1a1a1a' }} title={PLATFORM_META[p]?.label || p}>
                        {PLATFORM_META[p]?.icon(18) || null}
                      </div>
                    ))}
                  </div>
                  <div className="cp-detail-earn-mid">
                    <div className="cp-detail-earn-rate">${campaign.payoutPer1K}/1K views</div>
                    <div className="cp-detail-earn-min">${campaign.payoutPer1K} Min payout</div>
                  </div>
                  <div className="cp-detail-earn-budget">
                    <div className="cp-detail-earn-budget-label">Budget left</div>
                    <div className="cp-detail-earn-budget-val">${remaining.toLocaleString()}</div>
                  </div>
                </div>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}

function ManageCampaignModal({ campaign, onClose }) {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decidingId, setDecidingId] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(true);

  useEffect(() => {
    axiosInstance.get(`/campaigns/${campaign._id}/applications`)
      .then(res => setApplications(res.data.applications || []))
      .catch(() => toast.error("Failed to load applications"))
      .finally(() => setLoading(false));
    axiosInstance.get(`/clip-submissions/campaign/${campaign._id}`)
      .then(res => setSubmissions(res.data.submissions || []))
      .catch(() => {})
      .finally(() => setLoadingSubmissions(false));
  }, [campaign._id]);

  const handleDecide = async (appId, status) => {
    setDecidingId(appId);
    try {
      await axiosInstance.post(`/campaigns/${campaign._id}/applications/${appId}/status`, { status });
      setApplications(prev => prev.map(a => a._id === appId ? { ...a, status } : a));
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update application");
    } finally {
      setDecidingId(null);
    }
  };

  return (
    <div className="cp-modal-overlay" onClick={onClose}>
      <div className="cp-modal cp-modal--manage" onClick={e => e.stopPropagation()}>
        <div className="cp-modal-header">
          <div>
            <h2>{campaign.title}</h2>
            <span className="cp-manage-brand">{campaign.brandName}</span>
          </div>
          <button className="cp-modal-close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="cp-manage-stats">
          <div className="cp-manage-stat">
            <span className="cp-manage-stat-val">{campaign.applicationCount}</span>
            <span className="cp-manage-stat-label">Applications</span>
          </div>
          <div className="cp-manage-stat">
            <span className="cp-manage-stat-val">${campaign.payoutPer1K}/1K</span>
            <span className="cp-manage-stat-label">Payout rate</span>
          </div>
          <div className="cp-manage-stat">
            <span className="cp-manage-stat-val">${campaign.totalBudget.toLocaleString()}</span>
            <span className="cp-manage-stat-label">Total budget</span>
          </div>
        </div>

        <div className="cp-manage-apps-title">Applicants</div>
        {loading ? (
          <div className="cp-manage-loading">Loading...</div>
        ) : applications.length === 0 ? (
          <div className="cp-manage-empty">No applications yet. Share your campaign!</div>
        ) : (
          <div className="cp-manage-apps-list">
            {applications.map(app => (
              <div key={app._id} className="cp-manage-app-row">
                <div className="cp-manage-app-user">
                  {app.user?.profilePicture ? (
                    <img src={app.user.profilePicture} alt="" className="cp-manage-app-avatar" />
                  ) : (
                    <div className="cp-manage-app-avatar-fallback">{app.user?.username?.[0]?.toUpperCase()}</div>
                  )}
                  <div>
                    <div className="cp-manage-app-username">@{app.user?.username}</div>
                    {app.message && <div className="cp-manage-app-msg">"{app.message}"</div>}
                  </div>
                </div>
                {app.status === "pending" ? (
                  <div className="cp-manage-app-actions">
                    <button
                      className="cp-manage-app-btn approve"
                      disabled={decidingId === app._id}
                      onClick={() => handleDecide(app._id, "approved")}
                    >
                      Approve
                    </button>
                    <button
                      className="cp-manage-app-btn reject"
                      disabled={decidingId === app._id}
                      onClick={() => handleDecide(app._id, "rejected")}
                    >
                      Reject
                    </button>
                  </div>
                ) : (
                  <span className={`cp-manage-app-status ${app.status}`}>{app.status}</span>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="cp-manage-apps-title">Submitted clips</div>
        {loadingSubmissions ? (
          <div className="cp-manage-loading">Loading...</div>
        ) : submissions.length === 0 ? (
          <div className="cp-manage-empty">No clips submitted yet.</div>
        ) : (
          <div className="cp-manage-subs-list">
            {submissions.map(s => (
              <div key={s._id} className="cp-manage-sub-row">
                <div className="cp-manage-sub-top">
                  <span className="cp-manage-sub-user">@{s.user?.username}</span>
                  <span className="cp-manage-sub-platform">{PLATFORM_LABELS[s.platform] || s.platform}</span>
                  <a href={s.url} target="_blank" rel="noreferrer" className="cp-manage-sub-url">{s.url}</a>
                  <span className={`cp-manage-app-status ${s.status}`}>{s.status}</span>
                </div>
                {(s.compliance?.notes || s.fraudSignal?.reasons?.length > 0 || s.reviewError) && (
                  <div className="cp-manage-sub-notes">
                    {s.compliance?.notes && <div>Compliance: {s.compliance.notes}</div>}
                    {s.fraudSignal?.reasons?.length > 0 && (
                      <div>Fraud signal ({s.fraudSignal.score}/100): {s.fraudSignal.reasons.join("; ")}</div>
                    )}
                    {s.reviewError && <div>Review error: {s.reviewError}</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function HeroCarousel({ campaigns, appliedIds, onApply, myIds, onManage }) {
  const [idx, setIdx] = useState(0);
  const featured = campaigns.slice(0, 4);
  if (!featured.length) return null;
  const c = featured[idx];
  const catColor = CATEGORY_COLORS[c.category] || CATEGORY_COLORS.Other;
  const banner = c.bannerImage || NICHE_COVER[c.category] || NICHE_COVER.Variety;
  const applied = appliedIds.has(c._id);
  const isOwner = myIds.has(c._id);

  return (
    <div className="cp-hero" style={{ backgroundImage: `url(${banner})` }}>
      <div className="cp-hero-overlay" />
      <div className="cp-hero-content">
        <div className="cp-hero-brand">
          <div className="cp-hero-logo">
            {c.brandLogo
              ? <img src={c.brandLogo} alt={c.brandName} onError={e => { e.target.style.display='none'; e.target.parentElement.querySelector('.cp-hero-logo-fallback').style.display='flex'; }} />
              : null}
            <span className="cp-hero-logo-fallback" style={c.brandLogo ? { display: 'none' } : {}}>{c.brandName?.[0]?.toUpperCase()}</span>
          </div>
          <div>
            <div className="cp-hero-brand-name-row">
              <span className="cp-hero-brand-name">{c.brandName}</span>
              <VerifiedBadge type={c.brandVerified} size={15} />
            </div>
            {c.brandUsername && <div className="cp-hero-brand-username">{c.brandUsername}</div>}
          </div>
        </div>
        <h2 className="cp-hero-title">{c.title}</h2>
        <div className="cp-hero-payout">
          <span className="cp-hero-payout-num">${c.payType === "pool" ? Number(c.totalBudget).toLocaleString() : c.payoutPer1K}</span>
          <span className="cp-hero-payout-label">{c.payType === "pool" ? " pool" : " per 1,000 views"}</span>
        </div>
        {isOwner ? (
          <button className="cp-hero-apply-btn manage" onClick={() => onManage(c)}>
            Manage Campaign
          </button>
        ) : (
          <button
            className={`cp-hero-apply-btn${applied ? " applied" : ""}`}
            onClick={() => !applied && onApply(c)}
            disabled={applied}
          >
            {applied ? "✓ Applied" : "Apply Now"}
          </button>
        )}
      </div>
      {featured.length > 1 && (
        <>
          <button className="cp-hero-arrow cp-hero-arrow--left" onClick={() => setIdx(i => (i - 1 + featured.length) % featured.length)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>
          <button className="cp-hero-arrow cp-hero-arrow--right" onClick={() => setIdx(i => (i + 1) % featured.length)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
          <div className="cp-hero-dots">
            {featured.map((_, i) => (
              <button
                key={i}
                className={`cp-hero-dot${i === idx ? " active" : ""}`}
                onClick={() => setIdx(i)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function CampaignsPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  // Campaigns v1 (admin-only for now): cards open the new brief page and
  // "Create" goes to Launch campaign. Everyone else keeps the old modals.
  const briefsOn = isAdminUser(user);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");
  const [sort, setSort] = useState("featured");
  const [platform, setPlatform] = useState(null);
  const [search, setSearch] = useState("");
  const [appliedIds, setAppliedIds] = useState(new Set());
  const [applicationsById, setApplicationsById] = useState(new Map());
  const [showCreate, setShowCreate] = useState(false);
  const [detailTarget, setDetailTarget] = useState(null);
  const [manageTarget, setManageTarget] = useState(null);
  const [myIds, setMyIds] = useState(new Set());
  const [catOpen, setCatOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const searchTimer = useRef(null);
  const catRef = useRef(null);
  const sortRef = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (catRef.current && !catRef.current.contains(e.target)) setCatOpen(false);
      if (sortRef.current && !sortRef.current.contains(e.target)) setSortOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const fetchCampaigns = async (cat, q, s, p) => {
    setLoading(true);
    try {
      const params = {};
      if (cat && cat !== "All") params.category = cat;
      if (q) params.search = q;
      if (s && s !== "featured") params.sort = s;
      if (p) params.platform = p;
      const res = await axiosInstance.get("/campaigns", { params });
      setCampaigns(res.data.campaigns || []);
    } catch {
      toast.error("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    axiosInstance.get("/campaigns/applied")
      .then(res => {
        const apps = res.data.applications || [];
        const ids = new Set(apps.map(a => a.campaign?._id || a.campaign));
        setAppliedIds(ids);
        setApplicationsById(new Map(apps.map(a => [a.campaign?._id || a.campaign, a])));
      })
      .catch(() => {});
    axiosInstance.get("/campaigns/mine")
      .then(res => {
        const ids = new Set((res.data.campaigns || []).map(c => c._id));
        setMyIds(ids);
      })
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    fetchCampaigns(category, search, sort, platform);
  }, [category, sort, platform]);

  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearch(q);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchCampaigns(category, q, sort, platform), 400);
  };

  const handleViewDetail = (campaign) => {
    if (briefsOn) return navigate(`/c/${campaign.slug || campaign._id}`);
    setDetailTarget(campaign);
  };

  const handleApplied = (campaignId) => {
    setAppliedIds(prev => new Set([...prev, campaignId]));
  };

  const handleCreated = (campaign) => {
    setCampaigns(prev => [campaign, ...prev]);
    setMyIds(prev => new Set([...prev, campaign._id]));
  };

  const hasFilters = category !== "All" || sort !== "featured" || platform !== null;

  const clearFilters = () => {
    setCategory("All");
    setSort("featured");
    setPlatform(null);
  };

  return (
    <div className="cp-layout">
      <Topbar />
      <div className="cp-main">
        <Leftbar />
        <div className="cp-content">

          {/* Hero carousel */}
          {!loading && campaigns.length > 0 && (
            <HeroCarousel
              campaigns={campaigns}
              appliedIds={appliedIds}
              onApply={handleViewDetail}
              myIds={myIds}
              onManage={setManageTarget}
            />
          )}

          {/* Search */}
          <div className="cp-search-wrap">
            <svg className="cp-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              className="cp-search-input"
              placeholder="Campaigns and creators"
              value={search}
              onChange={handleSearchChange}
            />
            {user && (
              <button className="cp-search-create-btn" onClick={() => (briefsOn ? navigate("/campaigns/launch") : setShowCreate(true))}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Create
              </button>
            )}
          </div>

          {/* Filter bar */}
          <div className="cp-filter-bar">
            {/* Category dropdown */}
            <div className="cp-dropdown-wrap" ref={catRef}>
              <button
                className={`cp-dropdown-btn${category !== "All" ? " active" : ""}`}
                onClick={() => { setCatOpen(o => !o); setSortOpen(false); }}
              >
                {category === "All" ? "Category" : category}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginLeft: 4 }}>
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
              {catOpen && (
                <div className="cp-dropdown-menu">
                  {CATEGORIES.map(c => (
                    <button
                      key={c}
                      className={`cp-dropdown-item${category === c ? " active" : ""}`}
                      onClick={() => { setCategory(c); setCatOpen(false); }}
                    >
                      {c === "All" ? "All Categories" : c}
                      {category === c && (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Sort dropdown */}
            <div className="cp-dropdown-wrap" ref={sortRef}>
              <button
                className={`cp-dropdown-btn${sort !== "featured" ? " active" : ""}`}
                onClick={() => { setSortOpen(o => !o); setCatOpen(false); }}
              >
                {SORT_OPTIONS.find(s => s.value === sort)?.label || "Sort"}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginLeft: 4 }}>
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
              {sortOpen && (
                <div className="cp-dropdown-menu">
                  <div className="cp-dropdown-label">Sort by</div>
                  {SORT_OPTIONS.map(s => (
                    <button
                      key={s.value}
                      className={`cp-dropdown-item${sort === s.value ? " active" : ""}`}
                      onClick={() => { setSort(s.value); setSortOpen(false); }}
                    >
                      {s.label}
                      {sort === s.value && (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Platform icons */}
            {PLATFORMS.map(p => (
              <button
                key={p.value}
                className={`cp-platform-btn${platform === p.value ? " active" : ""}`}
                onClick={() => setPlatform(prev => prev === p.value ? null : p.value)}
                title={p.value}
              >
                {p.icon}
              </button>
            ))}

            {/* Clear */}
            {hasFilters && (
              <button className="cp-filter-clear" onClick={clearFilters}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
                Clear
              </button>
            )}
          </div>

          {/* Grid */}
          {loading ? (
            <div className="cp-loading">
              {[...Array(8)].map((_, i) => <div key={i} className="cp-card-skeleton" />)}
            </div>
          ) : campaigns.length === 0 ? (
            <div className="cp-empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5">
                <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
              </svg>
              <p>No campaigns found</p>
              {user && <button className="cp-create-btn" onClick={() => (briefsOn ? navigate("/campaigns/launch") : setShowCreate(true))}>Create the first one</button>}
            </div>
          ) : (
            <div className="cp-grid">
              {campaigns.map(c => (
                <CampaignCard
                  key={c._id}
                  campaign={c}
                  appliedIds={appliedIds}
                  onApply={handleViewDetail}
                  onManage={setManageTarget}
                  isOwner={myIds.has(c._id)}
                  onViewDetail={handleViewDetail}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {showCreate && <CreateCampaignModal onClose={() => setShowCreate(false)} onCreated={handleCreated} />}
      {detailTarget && (
        <CampaignDetailModal
          campaign={detailTarget}
          applied={appliedIds.has(detailTarget._id)}
          application={applicationsById.get(detailTarget._id)}
          onClose={() => setDetailTarget(null)}
          onApplied={handleApplied}
        />
      )}
      {manageTarget && <ManageCampaignModal campaign={manageTarget} onClose={() => setManageTarget(null)} />}
    </div>
  );
}

export { PLATFORM_META, NICHE_COVER };
